import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { EntityList, PageHead } from '../../components/EntityList';
import DetailPageLoader from '../../components/DetailPageLoader/DetailPageLoader';
import { useLabels } from '../../hooks/useLabels';
import BillService from '../../services/bill.service';
import { getError } from '../../utils';
import { toast } from '../../toasts';
import BillsEditor from './BillsEditor';

/**
 * Edits the recurring gasto (its BillTemplate) rather than one generated bill.
 *
 * The template is fetched here and handed to BillsEditor as `initialData`,
 * because the editor's own fetch path loads a Bill by id — a template is a
 * different document. `type: 'Periodico'` is stamped on so billToForm derives
 * startMonth/startYear the same way it does for a periodic bill.
 */
function EditBillTemplate() {
	const { t } = useLabels();
	const { id } = useParams();
	const navigate = useNavigate();
	const { state } = useLocation();
	const returnTo = state?.returnTo || '/AdminScreen/bills';

	const [template, setTemplate] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');

	useEffect(() => {
		let isCurrent = true;
		(async () => {
			try {
				const { data } = await BillService.getBillTemplate(id);
				if (isCurrent) setTemplate({ ...data, type: 'Periodico' });
			} catch (ex) {
				const message = getError(ex);
				if (isCurrent) setError(message);
				toast.error(message);
			} finally {
				if (isCurrent) setLoading(false);
			}
		})();
		return () => {
			isCurrent = false;
		};
	}, [id]);

	return (
		<EntityList>
			<Helmet>
				<title>{`Editar plantilla de ${t('entity.bill')}`}</title>
			</Helmet>
			<PageHead
				breadcrumb={[
					{ label: 'Inicio', to: '/AdminScreen' },
					{ label: t('entity.asset.plural'), to: '/AdminScreen/assets' },
					{ label: `Editar plantilla de ${t('entity.bill')}` },
				]}
				title={`Editar plantilla de ${t('entity.bill')}`}
				subtitle='Los cambios se aplican solo a los gastos que se generen a futuro.'
			/>
			{loading ? (
				<DetailPageLoader entityLabel='editor' />
			) : error ? (
				<div className='tw:rounded-[7px] tw:border tw:border-danger tw:bg-danger-soft tw:px-4 tw:py-3 tw:text-[13px] tw:text-danger'>
					{error}
				</div>
			) : (
				<BillsEditor
					mode='edit'
					scope='template'
					billId={id}
					initialData={template}
					onCancel={() => navigate(returnTo)}
					onSaved={() => navigate(returnTo)}
					saveLabel='Guardar'
				/>
			)}
		</EntityList>
	);
}

export default EditBillTemplate;
